import {
  menu,
  popMenu,
  popupTargetFromElement,
} from '@blocksuite/affine-components/context-menu';
import type { InsertToPosition } from '@blocksuite/affine-shared/utils';
import { AddCursorIcon } from '@blocksuite/icons/lit';
import { effect, signal } from '@preact/signals-core';
import type { TemplateResult } from 'lit';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html } from 'lit/static-html.js';

import type { Group, GroupTrait } from '../../../core/group-by/trait.js';
import {
  createUniComponentFromWebComponent,
  renderUniLit,
} from '../../../core/index.js';
import {
  computePostDropShifts,
  LivePreviewReorderController,
  type ReorderGeometry,
  type ReorderHost,
} from '../../../core/row-reorder/index.js';
import {
  getShiftKeyActive$,
  RowSelectionController,
  type RowSelectionHost,
} from '../../../core/row-selection/index.js';
import {
  DataViewUIBase,
  DataViewUILogicBase,
} from '../../../core/view/data-view-base.js';
import {
  TableViewRowSelection,
  type TableViewSelectionWithType,
} from '../selection';
import type { TableSingleView } from '../table-view-manager.js';
import { handleTableWheel } from '../utils/wheel.js';
import { TableClipboardController } from './controller/clipboard.js';
import { TableDragController } from './controller/drag.js';
import { TableHotkeysController } from './controller/hotkeys.js';
import { TableSelectionController } from './controller/selection.js';
import {
  addGroupIconStyle,
  addGroupStyle,
  groupsHiddenMessageStyle,
  tableGroupsContainerStyle,
  tableScrollContainerStyle,
  tableViewStyle,
  tableWrapperStyle,
} from './table-view-style';

interface RowRectSnapshot {
  readonly rowId: string;
  readonly top: number;
  readonly bottom: number;
  readonly height: number;
}

type RowRectsByGroup = ReadonlyMap<
  string | undefined,
  readonly RowRectSnapshot[]
>;

export class TableViewUILogic extends DataViewUILogicBase<
  TableSingleView,
  TableViewSelectionWithType
> {
  ui$ = signal<TableViewUI>();
  scrollContainer$ = signal<HTMLDivElement>();
  tableContainer$ = signal<HTMLDivElement>();

  clipboardController = new TableClipboardController(this);
  dragController = new TableDragController(this);
  hotkeysController = new TableHotkeysController(this);
  selectionController = new TableSelectionController(this);

  /**
   * View-agnostic live-preview row reorder. The table drag handler feeds
   * pointer events into this controller; its `liveShifts$` signal drives
   * per-row `translateY` so the rows themselves are the preview. Table
   * rows are the single visual unit in this view — no floating ghost is
   * mounted here; the rows the user already sees slide to the post-drop
   * positions as the cursor moves.
   */
  readonly reorder = new LivePreviewReorderController();

  /**
   * Shared modifier-aware multi-select. Click / Shift+Click / Ctrl+Click
   * all route through here; TableViewUI mirrors its ids back into the
   * existing TableViewRowSelection so checkboxes, context menu, and
   * every other caller see the same set. This is the single entry
   * point for row-level selection — reorder reads from
   * `rowSelection.selectedIds$` and any selection (contiguous or with
   * gaps like rows 1, 2, 4) is therefore moveable as a block.
   */
  readonly rowSelection = new RowSelectionController(
    this.buildRowSelectionHost()
  );

  private buildRowSelectionHost(): RowSelectionHost {
    return {
      getRowIdsInOrder: () => this.view.getBoundedRowIdsWindow().rowIds,
    };
  }

  /**
   * Public so the sync effects and the reorder host can resolve a
   * row's group when mirroring ids → RowWithGroup[]. Stays cheap —
   * the GroupTrait's `groupDataMap$` is already a signal.
   */
  findRowGroup(rowId: string): string | undefined {
    const map = this.view.groupTrait.groupDataMap$.value;
    if (!map) return undefined;
    for (const [key, group] of Object.entries(map)) {
      if (group?.rows.some(r => r.rowId === rowId)) return key;
    }
    return undefined;
  }

  private get readonly() {
    return this.view.readonly$.value;
  }

  clearSelection = () => {
    this.selectionController.clear();
  };

  addRow = (position: InsertToPosition) => {
    if (this.readonly) return;
    if (!this.view.rowCanAdd()) {
      const reason = this.view.rowAddDisabledReason();
      if (reason) this.root.config.notification.toast(reason);
      return;
    }
    const rowId = this.view.rowAdd(position);
    if (rowId) {
      this.root.openDetailPanel({
        view: this.view,
        rowId,
      });
    }
    return rowId;
  };

  focusFirstCell = () => {
    this.selectionController.focusFirstCell();
  };

  showIndicator = (evt: MouseEvent) => {
    return this.dragController.showIndicator(evt) != null;
  };

  hideIndicator = () => {
    this.dragController.dropPreview.remove();
  };

  moveTo = (id: string, evt: MouseEvent) => {
    const result = this.dragController.getInsertPosition(evt);
    if (result) {
      const row = this.view.rowGetOrCreate(id);
      row.move(result.position, undefined, result.groupKey);
    }
  };

  onWheel = (event: WheelEvent) => {
    handleTableWheel(event);
  };

  renderAddGroup = (groupHelper: GroupTrait) => {
    const addGroup = groupHelper.addGroup;
    if (!addGroup) {
      return;
    }
    const add = (e: MouseEvent) => {
      const ele = e.currentTarget as HTMLElement;
      popMenu(popupTargetFromElement(ele), {
        options: {
          preferDesktop: true,
          items: [
            menu.input({
              onComplete: text => {
                const column = groupHelper.property$.value;
                if (column) {
                  column.dataUpdate(() =>
                    addGroup({
                      text,
                      oldData: column.data$.value,
                      dataSource: this.view.manager.dataSource,
                    })
                  );
                }
              },
            }),
          ],
        },
      });
    };
    return html` <div style="display:flex;">
      <div class="${addGroupStyle}" @click="${add}">
        <div class="${addGroupIconStyle}">${AddCursorIcon()}</div>
        <div>New Group</div>
      </div>
    </div>`;
  };

  /**
   * Entry point used by `TableDragController` when the user presses
   * pointer on a drag handle. Takes one snapshot of every visible row's
   * rect — those frozen rects then drive both hit-testing and live
   * shift computation for the whole drag session. Re-measuring mid-
   * drag would include the already-applied `translateY` and compound
   * the offset into a runaway feedback loop.
   */
  startRowReorder(
    seedRowId: string,
    evt: PointerEvent,
    captureTarget: HTMLElement
  ): void {
    const ui = this.ui$.value;
    if (!ui) return;
    const snapshot = ui.captureRowRects();
    // If the seed row is already part of the active row selection (or
    // becomes a single-row drag via plain click), promote it in the
    // selection first so Shift+Click-built sets + checkbox-built sets
    // + drag on any selected row ALL move the same block.
    const selected = this.rowSelection.selectedIds$.value;
    if (!selected.has(seedRowId)) {
      this.rowSelection.select(seedRowId, {});
    }
    this.reorder.start({
      seedRowId,
      selectedIds: this.rowSelection.selectedIds$.value,
      pointer: evt,
      geometry: buildTableReorderGeometry(snapshot),
      host: this.reorderHost(),
      captureTarget,
    });
  }

  private reorderHost(): ReorderHost {
    const view = this.view;
    return {
      getRowIdsInOrder: scope => {
        if (scope != null) {
          const g = view.groupTrait.groupDataMap$.value?.[scope];
          return (g?.rows ?? []).map(r => r.rowId);
        }
        return view.getBoundedRowIdsWindow().rowIds;
      },
      getRowTitles: ids => this.readRowTitles(ids),
      moveRows: (rowIds, target, toGroup) => {
        const idSet = new Set(rowIds);
        const allRows = view.getBoundedRowsWindow().rows;
        const orderedDragged = allRows.filter(r => idSet.has(r.rowId));
        for (const row of orderedDragged) {
          const fromGroup = this.findRowGroup(row.rowId);
          const liveRow = view.rowGetOrCreate(row.rowId);
          liveRow.move(target, fromGroup, toGroup);
        }
      },
    };
  }

  readRowTitles(ids: readonly string[]): string[] {
    const view = this.view;
    const titleCol = view.mainProperties$.value.titleColumn;
    return ids.map(id => {
      if (!titleCol) return 'Untitled';
      return (
        view.cellGetOrCreate(id, titleCol).stringValue$.value || 'Untitled'
      );
    });
  }

  renderer = createUniComponentFromWebComponent(TableViewUI);
}

/**
 * Build a `ReorderGeometry` that works entirely off the pointer-down
 * snapshot. The closure captures row rects per group, hit-tests with
 * those static rects, and delegates shift computation to the shared
 * {@link computePostDropShifts} helper so Gantt + Table run the same
 * post-drop layout math.
 */
function buildTableReorderGeometry(snapshot: RowRectsByGroup): ReorderGeometry {
  return {
    pointToTargetIndex: (_x, clientY) => {
      for (const [scope, rects] of snapshot) {
        if (rects.length === 0) continue;
        const first = rects[0]!;
        const last = rects[rects.length - 1]!;
        if (clientY < first.top) return { index: 0, scope };
        if (clientY > last.bottom) return { index: rects.length, scope };
        for (let i = 0; i < rects.length; i++) {
          const r = rects[i]!;
          if (clientY <= r.bottom) {
            const mid = (r.top + r.bottom) / 2;
            return { index: clientY < mid ? i : i + 1, scope };
          }
        }
      }
      return null;
    },
    getLiveShift: state => {
      const out = new Map<string, { dx: number; dy: number }>();
      if (state.targetIndex === null) return out;
      const rects = snapshot.get(state.dropScope);
      if (!rects || rects.length === 0) return out;
      const dyMap = computePostDropShifts(rects, state);
      for (const [rowId, dy] of dyMap) {
        out.set(rowId, { dx: 0, dy });
      }
      return out;
    },
  };
}

export class TableViewUI extends DataViewUIBase<TableViewUILogic> {
  override connectedCallback(): void {
    super.connectedCallback();
    this.logic.ui$.value = this;
    this.logic.clipboardController.hostConnected();
    this.logic.dragController.hostConnected();
    this.logic.hotkeysController.hostConnected();
    this.logic.selectionController.hostConnected();
    this.classList.add('affine-database-table', tableViewStyle);
    this.dataset['testid'] = 'dv-table-view';

    // Surface the Shift key so CSS can swap the drag-handle / checkbox
    // cursor to a range-select affordance while the modifier is held —
    // matches the Gantt view's UX.
    this.disposables.add(
      effect(() => {
        const active = getShiftKeyActive$().value;
        if (active) this.dataset['shiftActive'] = 'true';
        else delete this.dataset['shiftActive'];
      })
    );

    // Two-way sync between the shared RowSelectionController (which
    // owns the Click / Shift / Ctrl logic) and the existing
    // TableViewRowSelection store (which every other code path —
    // checkboxes, context menu, clipboard, reorder — already reads).
    // The id-equality guard on each side prevents cyclic writes: an
    // update originating in either store propagates exactly once.
    this.disposables.add(
      effect(() => {
        const ids = this.logic.rowSelection.selectedIds$.value;
        const currentSel = this.logic.selectionController.selection;
        const currentIds = TableViewRowSelection.is(currentSel)
          ? new Set(currentSel.rows.map(r => r.id))
          : new Set<string>();
        if (
          ids.size === currentIds.size &&
          [...ids].every(id => currentIds.has(id))
        ) {
          return;
        }
        if (ids.size === 0) {
          if (TableViewRowSelection.is(currentSel)) {
            this.logic.selectionController.selection = undefined;
          }
          return;
        }
        const rows = [...ids].map(id => ({
          id,
          groupKey: this.logic.findRowGroup(id),
        }));
        this.logic.selectionController.selection = TableViewRowSelection.create(
          { rows }
        );
      })
    );
    this.disposables.add(
      effect(() => {
        const sel = this.logic.selection$.value;
        if (!TableViewRowSelection.is(sel)) {
          if (this.logic.rowSelection.selectedIds$.value.size > 0) {
            this.logic.rowSelection.setSelected(new Set());
          }
          return;
        }
        const selIds = new Set(sel.rows.map(r => r.id));
        const rsIds = this.logic.rowSelection.selectedIds$.value;
        if (
          selIds.size === rsIds.size &&
          [...selIds].every(id => rsIds.has(id))
        ) {
          return;
        }
        this.logic.rowSelection.setSelected(selIds);
      })
    );

    // Shift-drag lasso: any Shift-held pointerdown that moves past a
    // 4 px deadzone opens a marquee rectangle. Every row whose bounds
    // intersect the live rectangle is added to whatever selection was
    // active before the lasso started (additive semantics, matching
    // Finder/Files). A plain Shift-click (no drag) falls through to
    // the row-level click handler so the existing range-select
    // semantics stay intact.
    this.disposables.addFromEvent(this, 'pointerdown', this._handleLassoStart);

    // Persist per-device scroll position. localStorage (not Y.Doc)
    // because scroll state is personal — a colleague on their own
    // machine shouldn't be yanked around by where I last looked.
    // The effect waits for the scroll container to be mounted, then
    // (a) restores the last-known left/top offsets and (b) wires a
    // throttled scroll listener that writes them back.
    if (!this.logic.view.featureFlags$.value.use_shared_canvas_viewport) {
      let scrollCleanup: (() => void) | null = null;
      this.disposables.add(
        effect(() => {
          const container = this.logic.scrollContainer$.value;
          // Rewire when the container ref changes (view re-render swaps
          // the DOM element). Previous listener must detach first.
          scrollCleanup?.();
          scrollCleanup = null;
          if (!container) return;
          const viewId = this.logic.view.id;
          const key = (suffix: string): string => `table.${suffix}:${viewId}`;
          // Restore on a single rAF so the container's scrollWidth /
          // scrollHeight have settled before we assign offsets —
          // otherwise the browser clamps them to 0 on first paint.
          requestAnimationFrame(() => {
            if (typeof localStorage === 'undefined') return;
            try {
              const l = Number(localStorage.getItem(key('scrollLeft')));
              const t = Number(localStorage.getItem(key('scrollTop')));
              if (Number.isFinite(l) && l >= 0) container.scrollLeft = l;
              if (Number.isFinite(t) && t >= 0) container.scrollTop = t;
            } catch {
              // Private-mode Safari / quota — scroll just starts at 0.
            }
          });
          let persistTimer: ReturnType<typeof setTimeout> | null = null;
          const onScroll = (): void => {
            if (persistTimer != null) clearTimeout(persistTimer);
            persistTimer = setTimeout(() => {
              persistTimer = null;
              if (typeof localStorage === 'undefined') return;
              try {
                localStorage.setItem(
                  key('scrollLeft'),
                  String(container.scrollLeft)
                );
                localStorage.setItem(
                  key('scrollTop'),
                  String(container.scrollTop)
                );
              } catch {
                // Ignore quota.
              }
            }, 500);
          };
          container.addEventListener('scroll', onScroll, { passive: true });
          scrollCleanup = () => {
            if (persistTimer != null) clearTimeout(persistTimer);
            container.removeEventListener('scroll', onScroll);
          };
        })
      );
      this.disposables.add({ dispose: () => scrollCleanup?.() });
    }
  }

  override disconnectedCallback(): void {
    this.logic.reorder.cancel();
    super.disconnectedCallback();
  }

  private readonly _handleLassoStart = (e: PointerEvent): void => {
    if (this.logic.view.readonly$.value) return;
    if (!e.shiftKey) return;
    if (e.button !== 0 || !e.isPrimary) return;

    const target = e.target as HTMLElement | null;
    if (!target) return;
    // Dedicated widgets handle Shift natively (range-select via the
    // drag handle or checkbox) — don't hijack those.
    if (target.closest('.data-view-table-view-drag-handler')) return;
    if (target.closest('.row-select-checkbox')) return;
    if (target.closest('input, textarea, [contenteditable="true"]')) return;

    // Kill the native Shift-extend text-selection the browser would
    // otherwise start; and clear any lingering selection so the
    // marquee visual doesn't fight with a blue text highlight.
    e.preventDefault();
    getSelection()?.removeAllRanges();

    const startX = e.clientX;
    const startY = e.clientY;
    const pointerId = e.pointerId;
    // Snapshot the set that was selected BEFORE the lasso. Every
    // pointermove recomputes selection = baseIds ∪ intersecting rows,
    // so cancelling the drag restores the original set without a
    // special-case clear.
    const baseIds = new Set(this.logic.rowSelection.selectedIds$.value);

    let armed = true;
    let marquee: HTMLDivElement | null = null;
    let lassoActive = false;

    const ensureMarquee = (): HTMLDivElement => {
      if (marquee) return marquee;
      const el = document.createElement('div');
      el.style.position = 'fixed';
      el.style.pointerEvents = 'none';
      el.style.zIndex = '9998';
      el.style.border = '1px solid var(--streamlain-v2-brand-primary)';
      el.style.background = 'var(--streamlain-v2-brand-primaryTint)';
      el.style.borderRadius = '2px';
      document.body.appendChild(el);
      marquee = el;
      return el;
    };

    const onMove = (mv: PointerEvent): void => {
      if (mv.pointerId !== pointerId) return;
      if (armed) {
        if (Math.hypot(mv.clientX - startX, mv.clientY - startY) < 4) return;
        armed = false;
        lassoActive = true;
      }
      const left = Math.min(startX, mv.clientX);
      const top = Math.min(startY, mv.clientY);
      const right = Math.max(startX, mv.clientX);
      const bottom = Math.max(startY, mv.clientY);
      const el = ensureMarquee();
      el.style.left = `${left}px`;
      el.style.top = `${top}px`;
      el.style.width = `${right - left}px`;
      el.style.height = `${bottom - top}px`;

      const rows = this.querySelectorAll<HTMLElement>('data-view-table-row');
      const hits = new Set<string>(baseIds);
      for (const row of rows) {
        const id = row.dataset['rowId'];
        if (!id) continue;
        const r = row.getBoundingClientRect();
        if (
          r.left < right &&
          r.right > left &&
          r.top < bottom &&
          r.bottom > top
        ) {
          hits.add(id);
        }
      }
      this.logic.rowSelection.setSelected(hits);
    };

    const cleanup = (up: PointerEvent): void => {
      if (up.pointerId !== pointerId) return;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', cleanup);
      window.removeEventListener('pointercancel', cleanup);
      marquee?.remove();
      marquee = null;
      if (!lassoActive) return;
      // A synthetic `click` fires on the original target after the
      // pointerup; swallow it once so the row's Shift+Click handler
      // doesn't flip the newly-added rows back off.
      const swallow = (ce: MouseEvent): void => {
        ce.preventDefault();
        ce.stopPropagation();
        window.removeEventListener('click', swallow, true);
      };
      window.addEventListener('click', swallow, true);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', cleanup);
    window.addEventListener('pointercancel', cleanup);
  };

  /**
   * Walk every visible row in the table (grouped or flat) once and
   * freeze its bounding rect. The returned snapshot is the sole source
   * of truth for a reorder session: hit-test + live-shift both read
   * from it, never from the live DOM.
   */
  captureRowRects(): RowRectsByGroup {
    const map = new Map<string | undefined, RowRectSnapshot[]>();
    const groups = this.querySelectorAll<HTMLElement>(
      'affine-data-view-table-group'
    );
    const collect = (scope: string | undefined, container: HTMLElement) => {
      // Query the specific row tag — a blanket `[data-row-id]` would
      // also match every `<dv-table-view-cell-container>` nested inside
      // the row (each cell mirrors its parent row id), which would
      // inflate the snapshot with phantom `items` and corrupt the
      // post-drop heights.
      const rows = container.querySelectorAll<HTMLElement>(
        'data-view-table-row'
      );
      const list: RowRectSnapshot[] = [];
      for (const el of rows) {
        const id = el.dataset['rowId'];
        if (!id) continue;
        const rect = el.getBoundingClientRect();
        list.push({
          rowId: id,
          top: rect.top,
          bottom: rect.bottom,
          height: rect.height,
        });
      }
      map.set(scope, list);
    };
    if (groups.length > 0) {
      for (const g of groups) {
        collect(g.dataset['groupKey'], g);
      }
    } else {
      collect(undefined, this);
    }
    return map;
  }

  private renderTable() {
    const groups = this.logic.view.groupTrait.groupsDataList$.value?.filter(
      (g): g is Group => g !== undefined
    );
    if (groups && groups.length) {
      return html`
        <div class="${tableGroupsContainerStyle}">
          ${repeat(
            groups,
            group => group.key,
            group =>
              html`<affine-data-view-table-group
                data-group-key="${group.key}"
                .tableViewLogic="${this.logic}"
                .group="${group}"
              ></affine-data-view-table-group>`
          )}
          ${this.logic.renderAddGroup(this.logic.view.groupTrait)}
        </div>
      `;
    }
    return html`<affine-data-view-table-group
      .tableViewLogic="${this.logic}"
    ></affine-data-view-table-group>`;
  }

  override render(): TemplateResult {
    const vPadding = this.logic.root.config.virtualPadding$.value;
    const wrapperStyle = styleMap({
      marginLeft: `-${vPadding}px`,
      marginRight: `-${vPadding}px`,
    });
    const containerStyle = styleMap({
      paddingLeft: `${vPadding}px`,
      paddingRight: `${vPadding}px`,
    });
    return html`
      ${this.logic.headerWidget
        ? renderUniLit(this.logic.headerWidget, {
            dataViewLogic: this.logic,
          })
        : ''}
      <div class="${tableWrapperStyle}" style="${wrapperStyle}">
        <div
          ${ref(this.logic.scrollContainer$)}
          class="${tableScrollContainerStyle}"
          @wheel="${this.logic.onWheel}"
        >
          <div
            ${ref(this.logic.tableContainer$)}
            class="affine-database-table-container"
            style="${containerStyle}"
          >
            ${this.logic.view.groupTrait.allHidden$.value
              ? html`<div class="${groupsHiddenMessageStyle}">
                  All groups are hidden
                </div>`
              : this.renderTable()}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dv-table-view-ui': TableViewUI;
  }
}
